import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";

const FIELDS = [
  ["subject", "Subject", 1], ["revision_subject", "Subject (revision)", 1],
  ["intro", "Opening paragraph", 2], ["revision_intro", "Opening paragraph (revision)", 2],
  ["equipment_line", "Equipment line (one item)", 1], ["items_line", "Items line (several items)", 1],
  ["rental_total_line", "Total line for rental quotes (one item)", 1], ["rental_items_total_line", "Total line for rental quotes (several items)", 1], ["options_line", "Total line for multi-option quotes", 1],
  ["body", "Body — use {{intro}} and {{equipment_line}} where those parts go", 12], ["footer", "Footer (under the signature)", 1],
];

export function QuotationEmailTemplateEditor() {
  const [data, setData] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/quotation-email-template").then((r) => { setData(r.data); setTpl(r.data.template); }).catch(() => {}); }, []);
  if (!tpl) return null;
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/settings/quotation-email-template", tpl); setTpl(r.data); toast.success("Email template saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <section className="mt-4 bg-white border border-slate-200 rounded-lg p-5" data-testid="quotation-email-template-editor">
      <div className="flex gap-3 mb-4">
        <div className="w-9 h-9 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"><FileSignature className="w-4 h-4" /></div>
        <div>
          <div className="font-semibold text-slate-900">Quotation cover email template</div>
          <p className="text-sm text-slate-500 mt-1">Default wording for emailed quotations. A line is dropped when one of its fields is empty. Fields: {data.merge_fields.map((f) => `{{${f}}}`).join(" ")}</p>
        </div>
      </div>
      <div className="space-y-3">
        {FIELDS.map(([k, label, rows]) => (
          <div key={k}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
            {rows === 1
              ? <Input value={tpl[k]} onChange={(e) => setTpl({ ...tpl, [k]: e.target.value })} data-testid={`email-tpl-${k}`} />
              : <textarea value={tpl[k]} rows={rows} onChange={(e) => setTpl({ ...tpl, [k]: e.target.value })} className="w-full border border-slate-200 rounded-md p-2 text-sm" data-testid={`email-tpl-${k}`} />}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={() => setTpl(data.defaults)} data-testid="email-tpl-reset">Restore default wording</Button>
        <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="email-tpl-save">{saving ? "Saving…" : "Save template"}</Button>
      </div>
    </section>
  );
}
